import type { LinkEntryOffer } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';
import { SESSION_TEST_IDS } from './contracts';

export interface SessionChoicePageProps {
  /** The entries the link still offers, in the order the operator put them (§5.1). */
  readonly entries: readonly LinkEntryOffer[];
  /** Which entry is being started right now - its card's button is the one that waits. */
  readonly startingId: string | null;
  readonly onStart: (experienceId: string) => void;
  /** A refusal of the start, worded from its code. */
  readonly refusal: string | null;
}

/**
 * The choice page of §5.1 (planche `PageDeChoix`) - « une page de choix neutre,
 * aux couleurs PMS et pas à celles d'une expérience ».
 *
 * Three rules the mockup carries and the code must not soften:
 *
 * - **The card is not clickable.** ONE button per experience, « Commencer », and
 *   the card around it is a frame. A whole card that navigates makes the button
 *   a decoration and gives the pointer a target whose edges nobody can see.
 * - **No pill and no date.** A tag, a version, an expiry are facts about the
 *   LINK and about the object, and the person is shown neither - she is not
 *   choosing a version (§5.1). The couple travels in the POST, not on screen.
 * - **The name and the short description come off the RESOLVED version**, which
 *   is what `GET /api/l/<secret>` already serves: a link pinned to `production`
 *   shows the production wording while a draft renames the experience under it.
 *
 * The line about leaving and coming back is the mockup's, and it is the only
 * promise this page makes - the one §5.3 keeps.
 */
export function SessionChoicePage({
  entries,
  startingId,
  onStart,
  refusal,
}: SessionChoicePageProps) {
  return (
    <section
      data-testid={SESSION_TEST_IDS.choice}
      className="mx-auto flex w-full max-w-3xl flex-col gap-6 pt-4 pb-16"
    >
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold text-h3 text-bleu-nuit sm:text-[28px] sm:leading-9">
          Choisissez votre parcours
        </h1>
        <p className="max-w-[62ch] text-body text-gray-700">
          Ce lien ouvre plusieurs expériences. Chacune est un entretien avec un agent
          conversationnel.
        </p>
      </div>

      {refusal !== null && (
        <p role="alert" className="text-rouge text-small">
          {refusal}
        </p>
      )}

      <ul className="flex list-none flex-col gap-3 p-0">
        {entries.map((entry, index) => (
          <li
            // An experience may be offered twice (two entries of one link), so
            // the id alone is not a key; the offer carries no other identity.
            key={`${entry.experienceId}:${index}`}
            data-testid={SESSION_TEST_IDS.choiceCard}
            className="flex flex-col items-start gap-4 rounded-lg border-2 border-gray-200 bg-white p-5 sm:flex-row sm:items-center sm:gap-5"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <h2 className="font-semibold text-bleu-nuit text-body-lg">{entry.name}</h2>
              {entry.description !== '' && (
                <p className="text-gray-600 text-small">{entry.description}</p>
              )}
            </div>
            <div className="shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={() => onStart(entry.experienceId)}
                disabled={startingId !== null}
              >
                {startingId === entry.experienceId ? 'Ouverture...' : 'Commencer'}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <p className="text-gray-500 text-caption">
        Vous pourrez quitter et revenir : la page retiendra où vous en étiez.
      </p>
    </section>
  );
}
